import type { HeapNode } from '@/service/utils/cache/types';

export class MinHeap<T> {
    private readonly heap: HeapNode<T>[] = [];

    constructor(heap: HeapNode<T>[] = []) {
        this.heap = heap;
        this.buildHeap();
    }

    public insert(key: T, priority: number): void {
        const node: HeapNode<T> = { key, priority };
        this.heap.push(node);
        this.bubbleUp();
    }

    private buildHeap(): void {
        for (let i = Math.floor(this.heap.length / 2) - 1; i >= 0; i--) {
            this.bubbleDown(i);
        }
    }

    public extractMin(): HeapNode<T> | undefined {
        if (this.heap.length === 0) return undefined;
        if (this.heap.length === 1) return this.heap.pop();

        const min = this.heap[0];
        const lastElement = this.heap.pop();

        if (lastElement !== undefined) {
            this.heap[0] = lastElement;
            this.bubbleDown();
        }

        return min;
    }

    public peek(): HeapNode<T> | undefined {
        return this.heap[0];
    }

    public size(): number {
        return this.heap.length;
    }

    private bubbleUp(): void {
        let index = this.heap.length - 1;
        const element = this.heap[index];

        while (index > 0) {
            const parentIndex = Math.floor((index - 1) / 2);
            const parent = this.heap[parentIndex];

            if (element && parent && element.priority >= parent.priority) break;

            if (parent) this.heap[index] = parent;
            index = parentIndex;
        }
        if (element) this.heap[index] = element;
    }

    private bubbleDown(startIndex = 0): void {
        let index = startIndex;
        const length = this.heap.length;
        const element = this.heap[index];

        while (true) {
            const leftChildIndex = 2 * index + 1;
            const rightChildIndex = 2 * index + 2;
            let swapIndex: number | null = null;

            if (leftChildIndex < length) {
                const leftChild = this.heap[leftChildIndex];
                if (leftChild && element && leftChild.priority < element.priority) {
                    swapIndex = leftChildIndex;
                }
            }

            if (rightChildIndex < length) {
                const rightChild = this.heap[rightChildIndex];
                if (rightChild && element) {
                    const currentSwapNode = swapIndex !== null ? this.heap[swapIndex] : null;

                    if (
                        (swapIndex === null && rightChild.priority < element.priority)
                        || (currentSwapNode && rightChild.priority < currentSwapNode.priority)
                    ) {
                        swapIndex = rightChildIndex;
                    }
                }
            }

            if (swapIndex === null) break;

            this.heap[index] = this.heap[swapIndex] as HeapNode<T>;
            index = swapIndex;
        }
        if (element) this.heap[index] = element;
    }
}
